import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { lazy, Suspense } from "react";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";

import SmoothScroll from "@/components/SmoothScroll";
import { AuthProvider } from "@/context/AuthContext";
import { UIProvider } from "@/context/UIContext";

import Layout from "@/components/Layout";
import Home from "@/pages/Home";
import Prestations from "@/pages/Prestations";
import PrestationDetail from "@/pages/PrestationDetail";
import Secteurs from "@/pages/Secteurs";
import Realisations from "@/pages/Realisations";
import APropos from "@/pages/APropos";
import Blog from "@/pages/Blog";
import BlogArticle from "@/pages/BlogArticle";
import Contact from "@/pages/Contact";
import Devis from "@/pages/Devis";
import MerciDevis from "@/pages/MerciDevis";
import CommercialPage from "@/pages/CommercialPage";
import { ALL_COMMERCIAL_PAGES } from "@/data/commercial-pages";
import Rejoignez from "@/pages/Rejoignez";
import City from "@/pages/City";
import CityLyon from "@/pages/cities/CityLyon";
import CSEPage from "@/pages/CSEPage";
import NoelLyon from "@/pages/NoelLyon";
import AnimationNoel from "@/pages/AnimationNoel";
import DecorationNoelMagasin from "@/pages/DecorationNoelMagasin";
import DecorationNoelEntreprise from "@/pages/DecorationNoelEntreprise";
import MentionsLegales from "@/pages/MentionsLegales";
import Voyages from "@/pages/Voyages";
import VoyageDetail from "@/pages/VoyageDetail";
import ServiceCity from "@/pages/ServiceCity";
import NotFound from "@/pages/NotFound";

import AdminLogin from "@/pages/admin/AdminLogin";
// Admin chargé à la demande : hors du bundle public (performance)
const Admin = lazy(() => import("@/pages/admin/Admin"));
import ProtectedRoute from "@/pages/admin/ProtectedRoute";

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <UIProvider>
          <BrowserRouter>
            <SmoothScroll>
              <Routes>
                <Route element={<Layout />}>
                  <Route path="/" element={<Home />} />
                  <Route path="/prestations" element={<Prestations />} />
                  <Route path="/prestations/:slug" element={<PrestationDetail />} />
                  <Route path="/secteurs" element={<Secteurs />} />
                  <Route path="/nos-realisations" element={<Realisations />} />
                  <Route path="/realisations" element={<Navigate to="/nos-realisations" replace />} />
                  <Route path="/a-propos" element={<APropos />} />
                  <Route path="/blog" element={<Blog />} />
                  <Route path="/blog/:slug" element={<BlogArticle />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/devis" element={<Devis />} />
                  <Route path="/merci-devis" element={<MerciDevis />} />
                  <Route path="/merci-demande-devis" element={<MerciDevis />} />
                  {ALL_COMMERCIAL_PAGES.map((p) => (
                    <Route key={p.slug} path={`/${p.slug}`} element={<CommercialPage />} />
                  ))}
                  {/* Anciennes URLs du site précédent — redirection douce vers les équivalents actuels */}
                  <Route path="/nous-contacter.html" element={<Navigate to="/contact" replace />} />
                  <Route path="/nous-contacter" element={<Navigate to="/contact" replace />} />
                  <Route path="/rejoignez-nous" element={<Rejoignez />} />
                  {/* /villes/lyon = page guide/destination (lieux, quartiers, logistique) qui soutient la page maîtresse */}
                  <Route path="/villes/lyon" element={<CityLyon />} />
                  {/* Noël : pages commerciales dédiées (segment statique prioritaire sur :city) */}
                  <Route path="/fete-de-noel-entreprise/lyon" element={<NoelLyon />} />
                  <Route path="/animation-noel-entreprise" element={<AnimationNoel />} />
                  <Route path="/decoration-noel-magasin" element={<DecorationNoelMagasin />} />
                  <Route path="/decoration-noel-entreprise" element={<DecorationNoelEntreprise />} />
                  {/* Offre CSE : hub national + pages locales différenciées */}
                  <Route path="/evenements-cse" element={<CSEPage />} />
                  <Route path="/evenements-cse/:ville" element={<CSEPage />} />
                  <Route path="/villes/:slug" element={<City />} />
                  <Route path="/voyages" element={<Voyages />} />
                  <Route path="/voyages/:slug" element={<VoyageDetail />} />
                  <Route path="/fete-de-noel-entreprise/:city" element={<ServiceCity />} />
                  <Route path="/team-building-entreprise/:city" element={<ServiceCity />} />
                  <Route path="/mentions-legales" element={<MentionsLegales />} />
                  <Route path="*" element={<NotFound />} />
                </Route>

                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin" element={<ProtectedRoute><Suspense fallback={<div className="flex min-h-screen items-center justify-center bg-mist text-ink/50">Chargement…</div>}><Admin /></Suspense></ProtectedRoute>} />
              </Routes>
            </SmoothScroll>
          </BrowserRouter>
          <Toaster position="top-right" theme="dark" richColors />
        </UIProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
